<template>
  <div
    v-if="canPdf || canSign"
    class="att-sign"
  >
    <!-- Situação da última versão enviada -->
    <span
      v-if="signature"
      class="badge att-sign__badge"
      :class="`badge-light-${statusColor}`"
      :title="signedTitle"
    >
      <feather-icon
        v-if="signed"
        icon="CheckCircleIcon"
        size="12"
        class="mr-25"
      />
      {{ statusLabel }}
    </span>
    <small
      v-if="signature && signature.error"
      class="text-danger att-sign__error"
    >{{ signature.error }}</small>

    <button
      v-if="canPdf"
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="busy"
      @click="openPdf"
    >
      <feather-icon
        icon="FileTextIcon"
        size="13"
        class="mr-25"
      />
      {{ $t('attendanceSignature.viewPdf') }}
    </button>

    <button
      v-if="signed"
      type="button"
      class="btn btn-sm btn-outline-primary"
      :disabled="busy"
      @click="downloadSigned"
    >
      <feather-icon
        icon="DownloadIcon"
        size="13"
        class="mr-25"
      />
      {{ $t('attendanceSignature.downloadSigned') }}
    </button>
    <button
      v-else-if="signature"
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="busy"
      :title="$t('attendanceSignature.refresh')"
      @click="refresh"
    >
      <feather-icon
        icon="RefreshCwIcon"
        size="13"
      />
    </button>

    <!-- Versão enviada e ainda não assinada: gerar outra com os dados de agora -->
    <button
      v-if="canSign && signature && !signed"
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="busy"
      @click="sign(true)"
    >
      {{ $t('attendanceSignature.newVersion') }}
    </button>

    <button
      v-if="canSign"
      type="button"
      class="btn btn-sm btn-primary"
      :disabled="busy"
      @click="sign(signed)"
    >
      <b-spinner
        v-if="busy"
        small
        class="mr-25"
      />
      <feather-icon
        v-else
        icon="Edit3Icon"
        size="13"
        class="mr-25"
      />
      {{ signed ? $t('attendanceSignature.signNew') : $t('attendanceSignature.sign') }}
    </button>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import AresSignPopupMixin from '@/mixins/AresSignPopupMixin'

const COLORS = {
  pending: 'secondary',
  awaiting_authorization: 'warning',
  signing: 'info',
  signed: 'success',
  error: 'danger',
}

/**
 * PDF do atendimento (a ocorrência inteira). "Assinar" envia o PDF ao Ares Sign
 * e abre a página dele numa janela: a assinatura acontece lá, e o Ares Sign
 * devolve o documento assinado. Pode ser enviado a qualquer momento; cada envio
 * novo é uma versão.
 */
export default {
  name: 'AttendanceSignatureActions',
  components: { BSpinner },
  mixins: [ApiErrorMixin, AresSignPopupMixin],
  props: {
    occurrenceId: { type: [Number, String], required: true },
  },
  data() {
    return { signature: null, busy: false }
  },
  computed: {
    // Quem vê o PDF e quem envia para assinatura são permissões separadas (tela de papéis).
    canPdf() {
      return this.$hasAbility('regulacao.atendimento_pdf')
    },
    canSign() {
      return this.$hasAbility('regulacao.atendimento_assinar')
    },
    signed() {
      return !!this.signature && this.signature.status === 'signed'
    },
    statusColor() {
      return COLORS[this.signature && this.signature.status] || 'secondary'
    },
    statusLabel() {
      return this.$t(`attendanceSignature.status.${this.signature.status}`)
    },
    signedTitle() {
      if (!this.signed) return ''
      const date = this.signature.signed_at
        ? new Date(this.signature.signed_at).toLocaleString('pt-BR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
        : '—'

      return this.$t('attendanceSignature.signedBy', { name: this.signature.signer_name || '—', date })
    },
  },
  watch: {
    occurrenceId() {
      this.stopListening()
      this.load()
      this.listen()
    },
  },
  mounted() {
    this.load()
    this.listen()
  },
  beforeDestroy() {
    this.stopListening()
  },
  methods: {
    base() {
      return `${this.$store.state.api}occurrences/${this.occurrenceId}/attendance`
    },

    errorTitle() {
      return this.$t('attendanceSignature.errorTitle')
    },

    // Aviso em tempo real: o Ares Sign devolveu o documento assinado (ou deu erro).
    listen() {
      this.channel = `occurrence.${this.occurrenceId}`
      this.onUpdated = (data) => {
        if (data && data.kind === 'attendance') this.signature = data.signature
      }
      Echo.private(this.channel).listen('.DocumentSignatureUpdated', this.onUpdated)
    },

    stopListening() {
      if (this.channel && this.onUpdated) {
        Echo.private(this.channel).stopListening('.DocumentSignatureUpdated', this.onUpdated)
      }
    },

    async load() {
      if (!this.canPdf && !this.canSign) return
      try {
        const res = await this.$http.get(`${this.base()}/signature`)
        this.signature = res.data.data
      } catch (e) {
        this.signature = null
      }
    },

    async openPdf() {
      // Abre a aba já no clique; depois do await o bloqueador de pop-ups barraria.
      const win = window.open('', '_blank')
      this.busy = true
      try {
        const res = await this.$http.get(`${this.base()}/pdf`, { responseType: 'blob' })
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        if (win) win.location = blobUrl
        else window.open(blobUrl)
      } catch (e) {
        if (win) win.close()
        this.$message(this.errorTitle(), this.$t('attendanceSignature.pdfError'), 'error')
      } finally {
        this.busy = false
      }
    },

    // Assina a versão enviada (abre a página do Ares Sign) ou, com `newVersion`
    // (ou se ainda não há versão), envia o atendimento como está agora e abre a assinatura.
    async sign(newVersion = false) {
      if (this.signature && newVersion) {
        const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('attendanceSignature.confirm.textNew'), {
          title: this.$t('attendanceSignature.confirm.title'),
          okTitle: this.$t('attendanceSignature.confirm.ok'),
          cancelTitle: this.$t('attendanceSignature.confirm.cancel'),
          centered: true,
          size: 'sm',
        })
        if (!confirmed) return
      }

      const win = this.openSignWindow()
      this.busy = true
      try {
        let signature = this.signature
        if (!signature || newVersion) {
          const res = await this.$http.post(`${this.base()}/signature`)
          signature = res.data.data
          this.signature = signature
        }

        if (win && signature.sign_url) {
          win.location = signature.sign_url
          this.watchSignWindow(win, this.refresh)
        } else {
          if (win) win.close()
          this.$message(this.errorTitle(), this.$t('attendanceSignature.popupBlocked'), 'warning')
        }
      } catch (e) {
        if (win) win.close()
        this.$message(this.errorTitle(), this.apiErrorText(e, this.$t('attendanceSignature.failed')), 'error')
      } finally {
        this.busy = false
      }
    },

    async refresh() {
      this.busy = true
      try {
        const res = await this.$http.get(`${this.base()}/signature`, { params: { refresh: 1 } })
        if (res.data.data) this.signature = res.data.data
      } catch (e) {
        this.$message(this.errorTitle(), this.apiErrorText(e, this.$t('attendanceSignature.refreshError')), 'error')
      } finally {
        this.busy = false
      }
    },

    async downloadSigned() {
      this.busy = true
      try {
        const res = await this.$http.get(`${this.base()}/signature/download?versao=assinado`, { responseType: 'blob' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        link.download = 'atendimento-assinado.pdf'
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (e) {
        this.$message(this.errorTitle(), this.$t('attendanceSignature.downloadError'), 'error')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.att-sign {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.att-sign__badge {
  font-size: 12px;
}

.att-sign__error {
  max-width: 260px;
}
</style>
